// Feature flag registry - single source of truth.
//
// Every flag the app understands lives here. The resolver in src/lib/featureFlags.ts
// reads env first, then runtime JSON, then falls back to `defaultEnabled` below.
//
// All flags default to `true` so the app keeps current behavior when nothing is
// configured. Set the matching env var to `false` to disable.

export type DisabledBehavior = 'hide' | 'coming-soon' | 'maintenance'

export interface FlagDefinition {
  /** Human-readable name shown in admin UIs / logs. */
  label: string
  /** What the user sees when this flag is off. Only meaningful for page flags. */
  disabledAs?: DisabledBehavior
  /** Defaults to true - a missing env var preserves current behavior. */
  defaultEnabled: boolean
  /** Page flags affect routing; section flags only conditionally render. */
  scope: 'page' | 'section' | 'integration'
  /** Path that this page flag controls (page scope only). */
  routePath?: string
}

export const FEATURE_FLAGS = {
  // --- Pages ---
  HOME: { label: 'Home', defaultEnabled: true, scope: 'page', routePath: '/' },
  SERVICES: { label: 'Services', defaultEnabled: true, scope: 'page', routePath: '/services' },
  TRICHOLOGY: { label: 'Trichology', defaultEnabled: true, scope: 'page', routePath: '/trichology' },
  BRANCHES: { label: 'Salons', defaultEnabled: true, scope: 'page', routePath: '/branches' },
  MEMBERSHIP: { label: 'Membership', defaultEnabled: true, scope: 'page', routePath: '/membership' },
  GIFT_CARDS: { label: 'Gift Cards', defaultEnabled: true, scope: 'page', routePath: '/gift-cards' },
  GIFT_GUIDE: { label: 'Gift Guide', defaultEnabled: true, scope: 'page', routePath: '/gift-guide' },
  SHOP: { label: 'Shop', defaultEnabled: true, scope: 'page', routePath: '/shop', disabledAs: 'maintenance' },
  ABOUT: { label: 'About', defaultEnabled: true, scope: 'page', routePath: '/about' },
  CAREERS: { label: 'Careers', defaultEnabled: true, scope: 'page', routePath: '/careers' },
  CONTACT: { label: 'Contact', defaultEnabled: true, scope: 'page', routePath: '/contact' },

  // --- Legal + guide pages ---
  // Admin's Pages tab already exposed toggles for these, but the routes weren't
  // gated, so the toggles wrote flags nothing read. Now wired via FeatureGate.
  HAIR_LENGTH_GUIDE: { label: 'Hair length guide', defaultEnabled: true, scope: 'page', routePath: '/hair-length-guide' },
  FAQ: { label: 'FAQ', defaultEnabled: true, scope: 'page', routePath: '/faq' },
  CANCELLATION_POLICY: { label: 'Cancellation policy', defaultEnabled: true, scope: 'page', routePath: '/cancellation-policy' },
  REFUND_POLICY: { label: 'Refund policy', defaultEnabled: true, scope: 'page', routePath: '/refund-policy' },
  PRIVACY: { label: 'Privacy', defaultEnabled: true, scope: 'page', routePath: '/privacy' },
  COOKIES: { label: 'Cookies', defaultEnabled: true, scope: 'page', routePath: '/cookies' },
  // Photo/video marketing consent, reached by QR code in the salon. Named
  // PHOTO_CONSENT rather than CONSENT because `consent` already means COOKIE
  // consent everywhere in this codebase (lib/consent, CookieConsentBanner).
  PHOTO_CONSENT: { label: 'Photo & video consent', defaultEnabled: true, scope: 'page', routePath: '/photo-consent' },

  // --- Sections (only the external-dependency ones, per ideation) ---
  HOME_HERO_VIDEO: { label: 'Home hero video', defaultEnabled: true, scope: 'section' },
  HOME_REELS: { label: 'Instagram reels carousel', defaultEnabled: true, scope: 'section' },
  MAPS: { label: 'Google Maps embeds', defaultEnabled: true, scope: 'section' },
  CHAT: { label: 'Live chat widget', defaultEnabled: false, scope: 'integration' },
  // Concierge chat pops open by itself on page load (the 2026-06-19 behaviour).
  // OFF: it starts minimised and the launcher nudges instead. Admin: Sections tab.
  CHAT_AUTO_OPEN: { label: 'Chat opens by itself on page load', defaultEnabled: false, scope: 'section' },
  // Russian in the language menus (admin Sections tab). OFF hides it from the
  // switchers only; /ru/ pages keep working. See lib/languages.ts.
  LANG_RU: { label: 'Russian in the language menu', defaultEnabled: true, scope: 'section' },
} as const satisfies Record<string, FlagDefinition>

export type FlagKey = keyof typeof FEATURE_FLAGS

/** All page flag keys, useful for router wiring. */
export const PAGE_FLAGS: ReadonlyArray<FlagKey> = (Object.keys(FEATURE_FLAGS) as FlagKey[])
  .filter((k) => FEATURE_FLAGS[k].scope === 'page')
